/** @jsxImportSource @emotion/react */
import type { Interpolation } from "@emotion/react";
import { HallowAnalyticsEvent } from "@packages/analytics";
import { Link } from "@packages/framework";
import type { TriviaSectionItem } from "@packages/sdk";
import type {
  ContentMediumProps,
  CSSColorStrict,
  TriviaLargeProps,
} from "@packages/ui";
import { Button, numericPercentages, TrophyIcon, zIndices } from "@packages/ui";
import { differenceInHours } from "date-fns/differenceInHours";

import type { BaseGetSectionItemProps } from "./Section.emotion";

export type GetSectionItemTriviaPropsProps =
  BaseGetSectionItemProps<TriviaSectionItem>;

const overlayButton: Interpolation = {
  left: numericPercentages[50],
  position: "absolute",
  top: numericPercentages[50],
  transform: "translate(-50%, -50%)",
  zIndex: zIndices[1],
};

export const getSectionItemTriviaProps = ({
  item,
  link,
  t,
  analytics,
}: GetSectionItemTriviaPropsProps): TriviaLargeProps => {
  const triviaUnstarted = item?.reference?.state === "UNSTARTED";
  const triviaFinished = item?.reference?.state === "FINISHED";

  const actions = link
    ? [
        <Button
          key="links"
          variant="white"
          onClick={() =>
            analytics.track({
              event: triviaUnstarted
                ? HallowAnalyticsEvent.TappedStartTrivia
                : triviaFinished
                  ? HallowAnalyticsEvent.TappedTriviaSummary
                  : HallowAnalyticsEvent.TappedResumeTrivia,
              properties: {
                game_id: item?.reference?.id,
                trivia_id: item?.reference?.id,
              },
            })
          }
        >
          {t(
            triviaUnstarted
              ? "web_start_trivia"
              : triviaFinished
                ? "web_review_trivia"
                : "resume_trivia",
          )}
        </Button>,
      ]
    : null;

  const tags = item?.reference
    ? [
        t("x_playing_plural", {
          x_playing_plural: item.reference.player_count,
        }),
        t("ends_in_hours", {
          ends_in_hours: differenceInHours(
            new Date(item.reference.ends_at),
            new Date(),
          ),
        }),
      ]
    : null;

  return {
    actions: actions as TriviaLargeProps["actions"],
    asChild: true,
    centerActions: actions as TriviaLargeProps["centerActions"],
    children: <Link href="/trivia" />,
    color: item?.reference?.images?.color_hex as CSSColorStrict,
    imgSrc: item?.reference?.images,
    summary: triviaFinished ? item?.reference?.summary : null,
    questions: !triviaUnstarted ? item?.reference?.questions : null,
    tags,
  };
};

export const getSmallSectionItemTriviaProps = ({
  t,
  format,
  item,
}: GetSectionItemTriviaPropsProps): ContentMediumProps => {
  const today = format(new Date(), "EEEE");
  const triviaFinished = item?.reference?.state === "FINISHED";
  const numCorrect = item?.reference?.questions?.reduce((total, q) => {
    if (q.answers.find((a) => a.id === q.user_answer?.answer_id)?.correct)
      return total + 1;
    return total;
  }, 0);
  return {
    title: t("trivia_card_day_daily_trivia", { 0: today }),
    description: triviaFinished
      ? t("trivia_result_subtitle", { 0: numCorrect })
      : t("game_resets_in_hours", {
          game_resets_in_hours: differenceInHours(
            new Date(item?.reference?.ends_at),
            new Date(),
          ),
        }),
    metadata: t("games_underline"),
    metadataInlineIcon: <TrophyIcon.Inline />,
    imgSrc: item?.reference?.images,
    asChild: true,
    children: <Link href="/trivia" />,
    imgOverlay: triviaFinished ? null : (
      <Button css={overlayButton} variant="secondary">
        {t("button_description_play")}
      </Button>
    ),
  };
};
